/* eslint-disable no-alert, no-restricted-syntax */
// TODO: replace alert() and window.confirm() with GardenModal
import React, { useState, useEffect } from 'react';
import { App as CapApp } from '@capacitor/app';
import yardrayLogo from './assets/yardray-logo.png';
import api from './services/api';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import PlantsList from './components/PlantsList';
import AddPlantForm from './components/AddPlantForm';
import PlantDetail from './components/PlantDetail';
// import DrawableMap from './components/DrawableMap';
import GardenChat from './components/GardenChat';
import Tasks from './components/Tasks';
import Settings from './components/Settings';
import VerifyEmail from './components/VerifyEmail';
import RequestPasswordReset from './components/RequestPasswordReset';
import ResetPassword from './components/ResetPassword';
import EditPlant from './components/EditPlant';
import AddTask from './components/AddTask';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import './App.css';
import LandingPage from './components/LandingPage';
import SeedStarting from './components/SeedStarting';
import Weather from './components/Weather';
import About from './components/About';
import Admin from './components/Admin';
import EditTask from './components/EditTask';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOffline = () => setIsOffline(true);
    const handleOnline = () => setIsOffline(false);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);

    const handleAuthLogout = () => {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setUser(null);
    };
    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, []);

  useEffect(() => {
    let handler;
    CapApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        CapApp.exitApp();
      }
    }).then(h => { handler = h; });
    return () => { if (handler) handler.remove(); };
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      {isOffline && (
        <div className="app-offline-banner">
          No internet connection — some features may be unavailable
        </div>
      )}
      <Routes>
        {/* Public routes - Always accessible */}
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<RequestPasswordReset />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />

        {/* Landing page for non-logged-in users */}
        {!user && (
          <>
            <Route path="/" element={<LandingPage onGetStarted={() => window.location.href = '/login'} />} />
            <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
          </>
        )}

        {/* Protected routes - Only when logged in */}
        {user && (
          <Route path="/*" element={<AuthenticatedApp user={user} handleLogout={handleLogout} />} />
        )}

        {/* Redirect to login if not authenticated and trying to access protected route */}
        {!user && <Route path="*" element={<Navigate to="/" replace />} />}
      </Routes>
    </Router>
  );
}

function AuthenticatedApp({ user, handleLogout }) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const [mobileUserMenuOpen, setMobileUserMenuOpen] = React.useState(false);
  const [manageMenuOpen, setManageMenuOpen] = React.useState(false);

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/plants/export/csv`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'yardening-plants.csv';
      a.click();
    } catch (error) {
      alert('Failed to export data');
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm("Are you sure you want to delete your account? This will permanently delete all your properties, plants, photos, and tasks. This cannot be undone.");
    if (!confirmed) return;
    const doubleConfirmed = window.confirm("Last chance — permanently delete everything?");
    if (!doubleConfirmed) return;
    try {
      const token = localStorage.getItem("token");
      await fetch(`${process.env.REACT_APP_API_URL}/auth/account`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      window.location.href = "/";
    } catch (error) {
      alert("Failed to delete account. Please try again.");
    }
  };

  const [bugReportOpen, setBugReportOpen] = React.useState(false);
  const [bugDescription, setBugDescription] = React.useState('');
  const [bugSubmitting, setBugSubmitting] = React.useState(false);

  const handleSubmitBug = async () => {
    if (bugDescription.trim().length < 10) return;
    setBugSubmitting(true);
    try {
      await api.post('/auth/report-bug', {
        description: bugDescription,
        page: window.location.pathname
      });
      setBugDescription('');
      setBugReportOpen(false);
      alert('Thank you for your report.');
    } catch (error) {
      alert('Failed to send report. Please try again.');
    } finally {
      setBugSubmitting(false);
    }
  };

  const [featureRequestOpen, setFeatureRequestOpen] = React.useState(false);
  const [featureDescription, setFeatureDescription] = React.useState('');
  const [featureSubmitting, setFeatureSubmitting] = React.useState(false);

  const handleSubmitFeature = async () => {
    if (featureDescription.trim().length < 10) return;
    setFeatureSubmitting(true);
    try {
      await api.post('/auth/request-feature', {
        description: featureDescription
      });
      setFeatureDescription('');
      setFeatureRequestOpen(false);
      alert('Thank you for your suggestion.');
    } catch (error) {
      alert('Failed to send request. Please try again.');
    } finally {
      setFeatureSubmitting(false);
    }
  };

  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const navLinks = [
    { to: '/', label: 'Dashboard', end: true },
    { to: '/plants', label: 'Plants' },
    { to: '/tasks', label: 'Tasks' },
    { to: '/weather', label: 'Weather' },
    { to: '/seed-starting', label: 'Seeds' },
    { to: '/properties', label: 'Properties' },
    { to: '/chat', label: 'Chat' },
  ];

  return (
    <div className="App">
      {/* Header */}
      <div className="app-header">
        <img
          src={yardrayLogo}
          alt="Yardray"
          className="app-logo"
          onClick={() => window.location.href = '/'}
        />
        {isMobile ? (
          <button
            className="app-hamburger-btn"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Menu"
          >
            <span className="app-hamburger-line" />
            <span className="app-hamburger-line" />
            <span className="app-hamburger-line" />
          </button>
        ) : (
          <div className="app-user-menu-wrap">
            <button className="app-user-menu-btn" onClick={() => setUserMenuOpen(!userMenuOpen)}>
              {user.email} ▾
            </button>
            {userMenuOpen && (
              <div className="app-user-dropdown">
                <div className="app-user-dropdown-email">{user.email}</div>
                <button className="app-dropdown-btn" onClick={() => { setUserMenuOpen(false); window.location.href='/forgot-password'; }}>Change Password</button>
                <button className="app-dropdown-btn" onClick={handleExportCSV}>Export My Data</button>
                <button className="app-dropdown-btn" onClick={() => { setUserMenuOpen(false); window.location.href="/about"; }}>About Yardray</button>
                <button className="app-dropdown-btn" onClick={() => { setUserMenuOpen(false); setBugReportOpen(true); }}>Report a Bug</button>
                <button className="app-dropdown-btn" onClick={() => { setUserMenuOpen(false); setFeatureRequestOpen(true); }}>Request a Feature</button>
                <div className="app-manage-wrap">
                  <button className="app-manage-btn" onClick={() => setManageMenuOpen(!manageMenuOpen)}>
                    Manage Account {manageMenuOpen ? '▴' : '▸'}
                  </button>
                  {manageMenuOpen && (
                    <div className="app-manage-submenu">
                      <button
                        className="app-manage-delete-btn"
                        onClick={() => { setUserMenuOpen(false); setManageMenuOpen(false); handleDeleteAccount(); }}
                      >
                        Delete Account
                      </button>
                    </div>
                  )}
                </div>
                <button className="app-dropdown-btn app-dropdown-btn--danger" onClick={() => { setUserMenuOpen(false); handleLogout(); }}>Logout</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile dropdown menu */}
      {isMobile && menuOpen && (
        <div className="app-mobile-menu">
          {navLinks.map(link => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) => 'mobile-nav-link' + (isActive ? ' mobile-nav-link--active' : '')}
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </NavLink>
          ))}
          <div className="app-mobile-user-section">
            <button
              className="app-mobile-email-btn"
              onClick={() => setMobileUserMenuOpen(!mobileUserMenuOpen)}
            >
              {user.email} {mobileUserMenuOpen ? '▴' : '▾'}
            </button>
            {mobileUserMenuOpen && (
              <div>
                <button className="app-mobile-action-btn" onClick={() => { setMenuOpen(false); setMobileUserMenuOpen(false); window.location.href='/forgot-password'; }}>Change Password</button>
                <button className="app-mobile-action-btn" onClick={handleExportCSV}>Export My Data</button>
                <button className="app-mobile-action-btn" onClick={() => { setMenuOpen(false); setMobileUserMenuOpen(false); window.location.href="/about"; }}>About Yardray</button>
                <button className="app-mobile-action-btn" onClick={() => { setMenuOpen(false); setMobileUserMenuOpen(false); setBugReportOpen(true); }}>Report a Bug</button>
                <button className="app-mobile-action-btn" onClick={() => { setMenuOpen(false); setMobileUserMenuOpen(false); setFeatureRequestOpen(true); }}>Request a Feature</button>
                <div>
                  <button className="app-mobile-manage-btn" onClick={() => setManageMenuOpen(!manageMenuOpen)}>
                    Manage Account {manageMenuOpen ? '▴' : '▸'}
                  </button>
                  {manageMenuOpen && (
                    <button
                      className="app-mobile-delete-btn"
                      onClick={() => { setMenuOpen(false); setMobileUserMenuOpen(false); setManageMenuOpen(false); handleDeleteAccount(); }}
                    >
                      Delete Account
                    </button>
                  )}
                </div>
                <button className="app-mobile-action-btn app-mobile-action-btn--danger" onClick={() => { setMenuOpen(false); setMobileUserMenuOpen(false); handleLogout(); }}>Logout</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Desktop navigation */}
      {!isMobile && (
        <nav className="app-desktop-nav">
          {navLinks.map(link => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) => 'nav-link' + (isActive ? ' nav-link--active' : '')}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
      )}

      {/* Routes */}
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/plants" element={<PlantsList />} />
        <Route path="/plants/add" element={<AddPlantForm />} />
        <Route path="/plants/:id" element={<PlantDetail />} />
        <Route path="/plants/:id/edit" element={<EditPlant />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/tasks/add" element={<AddTask />} />
        <Route path="/tasks/:id/edit" element={<EditTask />} />
        {/* <Route path="/map" element={<DrawableMap />} /> */}
        <Route path="/chat" element={<GardenChat />} />
        <Route path="/properties" element={<Settings />} />
        <Route path="/weather" element={<Weather />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        <Route path="/seed-starting" element={<SeedStarting />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/about" element={<About />} />
      </Routes>

      {bugReportOpen && (
        <div className="app-modal-overlay">
          <div className="app-modal-dialog">
            <div className="app-modal-header">
              <h3>Report a Bug</h3>
            </div>
            <div className="app-modal-body">
              <p className="app-modal-hint">Describe what happened and what you expected to happen.</p>
              <textarea
                className="app-modal-textarea"
                value={bugDescription}
                onChange={(e) => setBugDescription(e.target.value)}
                rows="5"
                placeholder="e.g. When I tap Add Plant the page goes blank..."
              />
              <div className="app-modal-actions">
                <button className="app-modal-btn-cancel" onClick={() => { setBugReportOpen(false); setBugDescription(''); }}>
                  Cancel
                </button>
                <button
                  className="app-modal-btn-submit"
                  onClick={handleSubmitBug}
                  disabled={bugSubmitting || bugDescription.trim().length < 10}
                >
                  {bugSubmitting ? 'Sending...' : 'Send Report'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {featureRequestOpen && (
        <div className="app-modal-overlay">
          <div className="app-modal-dialog">
            <div className="app-modal-header">
              <h3>Request a Feature</h3>
            </div>
            <div className="app-modal-body">
              <p className="app-modal-hint">What would you like to see in Yardray?</p>
              <textarea
                className="app-modal-textarea"
                value={featureDescription}
                onChange={(e) => setFeatureDescription(e.target.value)}
                rows="5"
                placeholder="e.g. I would love to track when I water each plant..."
              />
              <div className="app-modal-actions">
                <button className="app-modal-btn-cancel" onClick={() => { setFeatureRequestOpen(false); setFeatureDescription(''); }}>
                  Cancel
                </button>
                <button
                  className="app-modal-btn-submit"
                  onClick={handleSubmitFeature}
                  disabled={featureSubmitting || featureDescription.trim().length < 10}
                >
                  {featureSubmitting ? 'Sending...' : 'Send Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="app-footer">
        <div className="app-footer-inner">
          <div className="app-footer-links">
            <a href="/terms">Terms of Service</a>
            <a href="/privacy">Privacy Policy</a>
            <a href="mailto:hello@yardray.app">Contact</a>
          </div>
          <p className="app-footer-copy">
            © {new Date().getFullYear()} Yardray. Made with love for yard enthusiasts everywhere.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
